import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ShipmentMap } from '@/components/features/shipments/ShipmentMap';
import {
  ShipmentStatusStepper,
  ShipmentQuickInfo,
  ShipmentInfoGrid,
  ShipmentPartyCard,
  ShipmentFinancials,
} from '@/components/features/shipments/details';
import { PrintLabelDialog, LABEL_OPTIONS, LabelType } from '@/components/features/shipments/PrintLabelDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowRight,
  Copy,
  Printer,
  Truck,
  ChevronDown,
  Clock,
  Phone,
  Calendar,
  Building,
  Package,
  MessageSquare,
  Tag,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Eye
} from 'lucide-react';
import { Shipment, ShipmentStatus } from '@/types/shipment.types';
import { Trip, TRIP_TYPE_LABELS, TRIP_STATUS_LABELS, MANIFEST_STATUS_LABELS } from '@/types/trip.types';
import { formatDate, formatPhone } from '@/lib/formatters';
import { ROUTES, PAYMENT_TYPE_LABELS, PAYMENT_STATUS_LABELS, STATUS_LABELS } from '@/lib/constants';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext';
import { useI18n } from '@/contexts/I18nContext';

// Arabic labels for each scan event type shown in the trips table

interface Driver {
  id: string;
  name: string;
  phone: string;
  current_shipments_count: number;
  coverage_areas: string[];
}

interface HistoryItem {
  status_code: string;
  status_name: string;
  color: string;
  note: string;
  created_at: string;
}

export default function ShipmentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, loading } = useApi();
  const { user } = useAuthContext();
  const { tp } = useI18n();
  const isTenant = user?.role === 'tenant';
  const [shipment, setShipment] = useState<Shipment | null>(null);
  // Surface the tracking number in the breadcrumb instead of the raw id
  useBreadcrumbLabel(id, shipment?.tracking_number);
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<{ lat: number; lng: number; title: string } | null>(null);
  const [showDriverDialog, setShowDriverDialog] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<{ code: string; name: string; color: string } | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [selectedLabelType, setSelectedLabelType] = useState<LabelType>('standard');
  const [trips, setTrips] = useState<Trip[]>([]);
  const [tripsLoaded, setTripsLoaded] = useState(false);

  // Notifications tab state
  interface ShipmentNotification {
    id: string;
    type: string;
    title: string;
    message: string;
    recipient_role: 'admin' | 'tenant' | 'carrier' | 'driver' | null;
    channels: string[];
    priority: 'low' | 'normal' | 'high' | 'urgent';
    created_at: string;
  }
  const [notifications, setNotifications] = useState<ShipmentNotification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);

  // Retry delivery dialog state
  const [showRetryDialog, setShowRetryDialog] = useState(false);
  const [retryNote, setRetryNote] = useState('');
  const [retryScheduledDate, setRetryScheduledDate] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [retrySubmitting, setRetrySubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      fetchShipment();
      fetchTrips();
    }
  }, [id]);

  const fetchTrips = async () => {
    if (!id) return;
    try {
      const result: any = await get('/api/v1/trips', { shipment_id: id });
      setTrips(result.data ?? []);
    } catch (error) {
      // silently ignore trips fetch error
    } finally {
      setTripsLoaded(true);
    }
  };

  const fetchShipment = async () => {
    try {
      const data: any = await get(`/api/v1/dashboard/${id}/shipment`);
      setShipment(data);

      if (data.status) {
        setCurrentStatus({
          code: data.status.code,
          name: STATUS_LABELS[data.status.code as ShipmentStatus] || data.status.name_ || data.status.name,
          color: data.status.color,
        });
      }

    } catch (error) {
      navigate(ROUTES.SHIPMENTS);
    }
  };

  const fetchDrivers = async () => {
    setLoadingDrivers(true);
    try {
      const response: any = await get('/api/v1/dashboard/drivers', { status: 'active' });
      setDrivers(response.data || []);
    } catch (error) {
      toast.error(tp.shipmentDetails.failedLoadingDrivers);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const fetchNotifications = async () => {
    if (!id) return;
    setLoadingNotifications(true);
    try {
      const response: any = await get(`/api/v1/dashboard/shipments/${id}/notifications`);
      setNotifications(response.notifications || []);
    } catch {
      toast.error(tp.shipmentDetails.failedLoadingNotifications);
    } finally {
      setLoadingNotifications(false);
      setNotificationsLoaded(true);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response: any = await get(`/api/v1/dashboard/shipments/${id}/history`);
      setHistory(response.history || []);
    } catch (error) {
      toast.error(tp.shipmentDetails.failedLoadingHistory);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Retry delivery handler
  const handleRetryDelivery = async () => {
    if (!shipment) return;
    setRetrySubmitting(true);
    try {
      await post(`/api/v1/dashboard/shipments/${shipment.id}/retry`, {
        note: retryNote.trim() || undefined,
        scheduled_date: retryScheduledDate || undefined,
      });
      toast.success(tp.shipmentDetails.retryScheduledSuccess);
      setShowRetryDialog(false);
      setRetryNote('');
      fetchShipment();
      fetchTrips();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || tp.shipmentDetails.retryScheduledSuccess);
    } finally {
      setRetrySubmitting(false);
    }
  };

  const handleAssignDriver = async (driverId: string) => {
    try {
      await post(`/api/v1/dashboard/${id}/assign-driver`, { driver_id: driverId });
      toast.success(tp.shipmentDetails.driverAssignedSuccess);
      setShowDriverDialog(false);
      fetchShipment();
    } catch (error) {
      toast.error(tp.shipmentDetails.failedAssignDriver);
    }
  };

  const handleCopyTrackingNumber = () => {
    if (shipment?.tracking_number) {
      navigator.clipboard.writeText(shipment.tracking_number);
      toast.success(tp.shipmentDetails.trackingCopied);
    }
  };

  const handlePrintLabel = (labelType: LabelType) => {
    setSelectedLabelType(labelType);
    setShowPrintDialog(true);
  };

  const openMapDialog = (lat: number, lng: number, title: string) => {
    if (lat && lng) {
      setSelectedAddress({ lat, lng, title });
      setShowMapDialog(true);
    }
  };

  const openDriverDialog = () => {
    setShowDriverDialog(true);
    fetchDrivers();
  };

  // Direct shipment detection: total_trips === 1 OR (trips loaded and length === 1 and trip is 'direct')
  const isDirectShipment = useMemo(() => {
    if (typeof shipment?.total_trips === 'number') {
      return shipment.total_trips === 1;
    }
    if (tripsLoaded && trips.length === 1) {
      return trips[0]?.trip_type === 'direct' || trips.length === 1;
    }
    // Unknown — default to false so FC statuses remain visible
    return false;
  }, [shipment?.total_trips, tripsLoaded, trips]);

  // Determine if "Retry Delivery" button should be shown
  const canRetry = useMemo(() => {
    if (!shipment) return false;
    // Only admin or carrier role (carrier_ops maps to carrier in this frontend)
    const allowedRoles: string[] = ['admin', 'carrier'];
    if (!user?.role || !allowedRoles.includes(user.role)) return false;
    // Shipment must be in failed state
    if (shipment.status?.code !== ShipmentStatus.FAILED) return false;
    // Latest delivery trip (direct or last_mile) must also be failed
    if (!tripsLoaded) return false;
    const deliveryTrips = trips.filter(
      (t) => t.trip_type === 'direct' || t.trip_type === 'last_mile'
    );
    if (deliveryTrips.length === 0) return false;
    const latestDeliveryTrip = deliveryTrips.reduce((prev, cur) =>
      cur.sequence > prev.sequence ? cur : prev
    );
    return latestDeliveryTrip.status === 'failed';
  }, [shipment, user?.role, trips, tripsLoaded]);

  const { stepperSteps, currentStep } = useMemo(() => {
    if (!shipment) return { stepperSteps: [], currentStep: 0 };

    const code = shipment.status?.code;
    const isReturnFlow = code === 'returning' || code === 'returned' || code === 'failed';

    const forwardSteps = isDirectShipment
      ? [
          { key: 'pending',          label: tp.shipmentDetails.pendingStatus },
          { key: 'out_for_pickup',   label: tp.shipmentDetails.outForPickup },
          { key: 'out_for_delivery', label: tp.shipmentDetails.outForDelivery },
          { key: 'delivered',        label: tp.shipmentDetails.delivered },
        ]
      : [
          { key: 'pending',          label: tp.shipmentDetails.pendingStatus },
          { key: 'out_for_pickup',   label: tp.shipmentDetails.outForPickup },
          { key: 'collected',        label: tp.shipmentDetails.collectedFromSender },
          { key: 'in_transit',       label: tp.shipmentDetails.inTransit },
          { key: 'out_for_delivery', label: tp.shipmentDetails.outForDelivery },
          { key: 'delivered',        label: tp.shipmentDetails.delivered },
        ];

    const returnSteps = [
      { key: 'pending',   label: tp.shipmentDetails.pendingStatus },
      { key: 'failed',    label: tp.shipmentDetails.deliveryFailed },
      { key: 'returning', label: tp.shipmentDetails.returning },
      { key: 'returned',  label: tp.shipmentDetails.returned },
    ];

    const steps = isReturnFlow ? returnSteps : forwardSteps;

    const statusToKey: Record<string, string> = {
      pending:          'pending',
      out_for_pickup:   'out_for_pickup',
      collected:        'collected',
      in_transit:       'in_transit',
      out_for_delivery: 'out_for_delivery',
      delivered:        'delivered',
      failed:           'failed',
      returning:        'returning',
      returned:         'returned',
      cancelled:        'pending',
    };

    const targetKey = statusToKey[code] ?? 'pending';
    const foundIdx = steps.findIndex((s) => s.key === targetKey);
    let currentStep = foundIdx === -1 ? 0 : foundIdx;

    const terminalKeys = new Set(['delivered', 'returned']);
    if (terminalKeys.has(targetKey) && foundIdx === steps.length - 1) {
      currentStep = steps.length;
    }

    return { stepperSteps: steps, currentStep };
  }, [shipment?.status?.code, isDirectShipment, tp]);

  // Quick info badges
  const quickInfoBadges = useMemo(() => {
    if (!shipment) return [];
    return [
      {
        key: 'delivery',
        label: tp.shipmentDetails.deliveredBadge,
        status: shipment.status?.code === ShipmentStatus.DELIVERED ? 'success' : 'pending',
      },
      {
        key: 'driver_settled',
        label: tp.shipmentDetails.driverSettled,
        status: shipment.driver_settled ? 'success' : 'pending',
      },
      {
        key: 'tenant_settled',
        label: tp.shipmentDetails.tenantSettled,
        status: shipment.tenant_settled ? 'success' : 'pending',
      },
      {
        key: 'collection',
        label: tp.shipmentDetails.codCollection,
        status: shipment.payment_status === 'paid' ? 'success' : 'pending',
      },
    ] as const;
  }, [shipment, tp]);

  // Shipment info fields for grid
  const shipmentInfoFields = useMemo(() => {
    if (!shipment) return [];
    // Backend may return service_type as { id, name } or as a code string;
    // accept either shape so the field always renders.
    const serviceTypeRaw: any = shipment.service_type;
    const serviceTypeName = typeof serviceTypeRaw === 'string'
      ? serviceTypeRaw
      : serviceTypeRaw?.name ?? null;
    return [
      { key: 'carrier', label: tp.shipmentDetails.carrier, value: shipment.carrier?.name || '-', icon: Truck },
      { key: 'from_city', label: tp.shipmentDetails.fromCity, value: shipment.from_city?.name || shipment.from_address?.city || '-', icon: Building },
      { key: 'to_city', label: tp.shipmentDetails.toCity, value: shipment.to_city?.name || shipment.to_address?.city || '-', icon: Building },
      { key: 'pieces', label: tp.shipmentDetails.piecesCount, value: shipment.items?.length || 0, icon: Package },
      { key: 'service_type', label: tp.shipmentDetails.serviceType, value: serviceTypeName || '-', icon: Tag },
      { key: 'created_at', label: tp.shipmentDetails.createdDate, value: formatDate(shipment.created_at, 'dd/MM/yyyy HH:mm'), icon: Calendar },
      { key: 'delivery_date', label: tp.shipmentDetails.expectedDeliveryDate, value: shipment.delivery_date ? formatDate(shipment.delivery_date, 'dd/MM/yyyy') : '-', icon: Calendar },
      { key: 'delivered_at', label: tp.shipmentDetails.actualDeliveryDate, value: shipment.delivered_at ? formatDate(shipment.delivered_at, 'dd/MM/yyyy HH:mm') : '-', icon: Calendar },
    ];
  }, [shipment, tp]);

  // Financial fields — RTL row-major 2-col grid:
  //   each row is [right, left]; left column reads:
  //   payment_status, shipment_value, delivery_fee, total
  const financialFields = useMemo(() => {
    if (!shipment) return [];
    return [
      // Row 1
      { key: 'payment_type', label: tp.shipmentDetails.paymentType, value: PAYMENT_TYPE_LABELS[shipment.actual_payment_type || ''] || shipment.actual_payment_type || '-', isCurrency: false },
      { key: 'payment_status', label: tp.shipmentDetails.paymentStatus, value: PAYMENT_STATUS_LABELS[shipment.payment_status] || shipment.payment_status, isCurrency: false },
      // Row 2
      { key: 'cod_amount', label: tp.shipmentDetails.codAmount, value: shipment.cod_amount, highlight: true },
      { key: 'shipment_value', label: tp.shipmentDetails.shipmentValue, value: shipment.shipment_value },
      // Row 3
      { key: 'platform_fee', label: tp.shipmentDetails.platformFee, value: shipment.platform_fee || 0 },
      { key: 'delivery_fees', label: tp.shipmentDetails.deliveryFees, value: shipment.delivery_fee || 0 },
      // Row 4
      { key: 'tenant_payout', label: tp.shipmentDetails.tenantPayout, value: shipment.tenant_payout || 0, type: 'success' as const },
      { key: 'total_amount', label: tp.shipmentDetails.total, value: shipment.total_amount || shipment.cod_amount, highlight: true },
    ];
  }, [shipment, tp]);

  if (loading) {
    return (
      <PageContainer>
        <LoadingSkeleton type="details" />
      </PageContainer>
    );
  }

  if (!shipment) return null;

  return (
    <PageContainer>
      <div className="space-y-6" dir="rtl">
        {/* Internal Header */}
        <div className="space-y-4">
          {/* Top row: Back button, tracking number, actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate(ROUTES.SHIPMENTS)}
                className="gap-1 shrink-0"
              >
                <ArrowRight className="h-4 w-4" />
                {tp.shipmentDetails.back}
              </Button>
              <div className="min-w-0">
                <h1 className="text-xl md:text-2xl font-bold truncate" dir="ltr">
                  {shipment.tracking_number}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {formatDate(shipment.created_at, 'dd MMMM yyyy - HH:mm')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <Button type="button" variant="outline" size="sm" onClick={handleCopyTrackingNumber} className="gap-1.5">
                <Copy className="h-4 w-4" />
                {tp.shipmentDetails.copy}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="gap-1.5">
                    <Printer className="h-4 w-4" />
                    {tp.shipmentDetails.print}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-popover">
                  {LABEL_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option.type}
                      onClick={() => handlePrintLabel(option.type)}
                      className="flex flex-col items-start gap-0.5 cursor-pointer py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Tag className="h-3.5 w-3.5" />
                        <span className="font-medium">{option.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground mr-5">{option.description}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Status Display — read-only; update via trip detail */}
              {currentStatus && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled
                  style={{ borderColor: currentStatus.color, color: currentStatus.color }}
                >
                  {currentStatus.name}
                </Button>
              )}

              {canRetry && (
                <Button
                  type="button"
                  onClick={() => setShowRetryDialog(true)}
                  variant="default"
                  size="sm"
                  className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <RefreshCw className="h-4 w-4" />
                  {tp.shipmentDetails.retryDelivery}
                </Button>
              )}

              {!isTenant && shipment?.status?.code === 'pending' && (
                <Button type="button" onClick={openDriverDialog} variant="default" size="sm" className="gap-1.5">
                  <Truck className="h-4 w-4" />
                  {tp.shipmentDetails.assignDriver}
                </Button>
              )}
            </div>
          </div>

          {/* Quick Info Badges */}
          <ShipmentQuickInfo badges={quickInfoBadges} />

          {/* Status Stepper */}
          <Card>
            <CardContent className="pt-4 pb-2">
              <ShipmentStatusStepper currentStep={currentStep} steps={stepperSteps} />
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="description" className="w-full" dir="rtl">
          <TabsList className="grid w-full grid-cols-3 h-auto">
            <TabsTrigger value="description" className="text-sm py-2">{tp.shipmentDetails.descriptionTab}</TabsTrigger>
            <TabsTrigger
              value="history"
              className="text-sm py-2"
              onClick={() => { if (history.length === 0) fetchHistory(); }}
            >
              {tp.shipmentDetails.historyTab}
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              className="text-sm py-2"
              onClick={() => { if (!notificationsLoaded) fetchNotifications(); }}
            >
              {tp.shipmentDetails.notificationsTab}
            </TabsTrigger>
          </TabsList>
          
          {/* Description Tab */}
          <TabsContent value="description" className="space-y-6 mt-6" dir="rtl">
            {/* Shipment Info Grid */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Package className="h-4 w-4" />
                  {tp.shipmentDetails.shipmentInfo}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ShipmentInfoGrid fields={shipmentInfoFields} columns={4} />
              </CardContent>
            </Card>

            {/* Tenant + Sender + Recipient — one row */}
            <div className="grid gap-4 md:grid-cols-3">
              {/* Customer / Tenant Card — address = warehouse snapshot saved on creation */}
              <ShipmentPartyCard
                title={tp.shipmentDetails.customer}
                name={shipment.tenant?.name}
                phone={shipment.tenant?.phone}
                email={shipment.tenant?.email}
                icon="store"
                address={{
                  street: shipment.from_address?.street,
                  neighborhood: shipment.from_neighborhood?.name || shipment.from_address?.neighborhood,
                  city: shipment.from_city?.name || shipment.from_address?.city,
                  lat: shipment.from_address?.lat,
                  lng: shipment.from_address?.lng,
                }}
                onMapClick={() => openMapDialog(shipment.from_address?.lat, shipment.from_address?.lng, tp.shipmentDetails.warehouseLocation)}
              />

              {/* Sender Card — pure snapshot from shipment.sender_info */}
              <ShipmentPartyCard
                title={tp.shipmentDetails.sender}
                name={shipment.sender_info?.name}
                phone={shipment.sender_info?.phone}
                email={shipment.sender_info?.email}
                address={{
                  street: shipment.from_address?.street,
                  neighborhood: shipment.from_neighborhood?.name || shipment.from_address?.neighborhood,
                  city: shipment.from_city?.name || shipment.from_address?.city,
                  lat: shipment.from_address?.lat,
                  lng: shipment.from_address?.lng,
                }}
                onMapClick={() => openMapDialog(shipment.from_address?.lat, shipment.from_address?.lng, tp.shipmentDetails.senderAddress)}
              />

              {/* Recipient Card */}
              <ShipmentPartyCard
                title={tp.shipmentDetails.recipientAddress}
                name={shipment.recipient_info?.name}
                phone={shipment.recipient_info?.phone}
                email={shipment.recipient_info?.email}
                address={{
                  street: shipment.to_address?.street,
                  neighborhood: shipment.to_neighborhood?.name || shipment.to_address?.neighborhood,
                  city: shipment.to_city?.name || shipment.to_address?.city,
                  lat: shipment.to_address?.lat,
                  lng: shipment.to_address?.lng,
                }}
                notes={shipment.recipient_info?.notes}
                onMapClick={() => openMapDialog(shipment.to_address?.lat, shipment.to_address?.lng, tp.shipmentDetails.recipientAddress)}
              />
            </div>

            {/* Financial Info — own row */}
            <ShipmentFinancials fields={financialFields} />

            {/* Items Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Package className="h-4 w-4" />
                  {tp.shipmentDetails.items.replace('{count}', String(shipment.items?.length || 0))}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-sm">{tp.shipmentDetails.itemName}</TableHead>
                      <TableHead className="text-sm">{tp.shipmentDetails.itemQuantity}</TableHead>
                      <TableHead className="text-sm">{tp.shipmentDetails.itemSku}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(shipment.items || []).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="text-sm font-medium">{item.name}</TableCell>
                        <TableCell className="text-sm">{item.quantity}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{item.sku || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Trips Section — within Description tab */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Truck className="h-4 w-4" />
                  {tp.shipmentDetails.trips.replace('{count}', String(trips.length))}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!tripsLoaded ? (
                  <p className="text-sm text-muted-foreground text-center py-4">{tp.shipmentDetails.loading}</p>
                ) : trips.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">{tp.shipmentDetails.noTrips}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-sm">{tp.shipmentDetails.tripSequence}</TableHead>
                        <TableHead className="text-sm">{tp.shipmentDetails.tripType}</TableHead>
                        <TableHead className="text-sm">{tp.shipmentDetails.tripRoute}</TableHead>
                        <TableHead className="text-sm">{tp.shipmentDetails.tripDriver}</TableHead>
                        <TableHead className="text-sm">{tp.shipmentDetails.manifest}</TableHead>
                        <TableHead className="text-sm">{tp.shipmentDetails.paymentStatus}</TableHead>
                        <TableHead className="text-sm w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trips.map((trip) => {
                        const fromNode = typeof trip.from_node === 'object' ? trip.from_node?.name : trip.from_node;
                        const toNode = typeof trip.to_node === 'object' ? trip.to_node?.name : trip.to_node;
                        const cleanVal = (v?: string | null) => (v && v !== '--' ? v : null);
                        const driverName = cleanVal(trip.driver_name) || cleanVal(trip.driver?.name) || cleanVal(shipment.driver?.name);
                        const driverPhone = cleanVal(trip.driver_phone) || cleanVal(trip.driver?.phone) || cleanVal(shipment.driver?.phone);
                        return (
                          <TableRow key={trip.id} className="hover:bg-muted/50">
                            <TableCell className="text-sm text-muted-foreground">{trip.sequence}</TableCell>
                            <TableCell className="text-sm">
                              <span className="flex items-center gap-2">
                                {TRIP_TYPE_LABELS[trip.trip_type] || trip.trip_type}
                                {(trip.trip_type === 'direct' || trip.trip_type === 'last_mile') &&
                                  trip.attempt_number != null && (
                                    <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                      {tp.shipmentDetails.attempt.replace('{number}', String(trip.attempt_number))}
                                    </Badge>
                                  )}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">
                              <span>{fromNode || '-'}</span>
                              <span className="text-muted-foreground mx-1">←</span>
                              <span>{toNode || tp.shipmentDetails.customer}</span>
                            </TableCell>
                            <TableCell className="text-sm">
                              {driverName ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="font-medium">{driverName}</span>
                                  {driverPhone && (
                                    <span className="text-xs text-muted-foreground flex items-center gap-1" dir="ltr">
                                      <Phone className="h-3 w-3" />
                                      {formatPhone(driverPhone)}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">{tp.shipmentDetails.unassigned}</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">
                              {trip.manifest_number ? (
                                <span className="font-mono text-xs">
                                  {trip.manifest_number}
                                  {trip.manifest_status && (
                                    <span className="mr-1 text-xs text-muted-foreground">
                                      ({MANIFEST_STATUS_LABELS[trip.manifest_status] ?? trip.manifest_status})
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">
                                {TRIP_STATUS_LABELS[trip.status] ?? trip.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              {/* Trip detail page is carrier-only — hide the icon for tenants */}
                              {!isTenant && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => navigate(ROUTES.TRIP_DETAILS(trip.id))}
                                  title={tp.shipmentDetails.viewTrip}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
                {tripsLoaded && trips.length > 0 && (
                  <div className="flex items-center justify-end gap-2 pt-3 mt-3 border-t text-sm">
                    <span className="text-muted-foreground">{tp.shipmentDetails.totalTrips}</span>
                    <span className="font-semibold">{trips.length}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6 mt-6" dir="rtl">
            {loadingHistory ? (
              <LoadingSkeleton type="card" />
            ) : history.length === 0 ? (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-sm text-muted-foreground">{tp.shipmentDetails.noHistory}</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Clock className="h-4 w-4" />
                    {tp.shipmentDetails.timeline}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative space-y-4">
                    <div className="absolute right-[15px] top-2 bottom-2 w-0.5 bg-border" />
                    
                    {history.map((item, index) => (
                      <div key={index} className="relative flex gap-4 pr-10">
                        <div 
                          className="absolute right-0 top-1 h-8 w-8 rounded-full border-4 border-background flex items-center justify-center"
                          style={{ backgroundColor: item.color }}
                        >
                          <div className="h-3 w-3 rounded-full bg-background" />
                        </div>
                        
                        <div className="flex-1 pb-6 border-b last:border-0">
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                              <p className="text-sm font-semibold" style={{ color: item.color }}>
                                {item.status_name}
                              </p>
                              {item.note && (
                                <p className="text-sm text-muted-foreground">{item.note}</p>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(item.created_at, 'dd MMM yyyy - HH:mm')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Notifications Tab — list of sent in-app / WhatsApp / email notifications */}
          <TabsContent value="messages" className="mt-6" dir="rtl">
            {loadingNotifications ? (
              <LoadingSkeleton type="card" />
            ) : !notificationsLoaded ? null : notifications.length === 0 ? (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-sm text-muted-foreground">{tp.shipmentDetails.noNotifications}</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <MessageSquare className="h-4 w-4" />
                    {tp.shipmentDetails.notificationsCount.replace('{count}', String(notifications.length))}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-sm">{tp.shipmentDetails.notificationType}</TableHead>
                        <TableHead className="text-sm">{tp.shipmentDetails.notificationChannels}</TableHead>
                        <TableHead className="text-sm">{tp.shipmentDetails.notificationRecipient}</TableHead>
                        <TableHead className="text-sm">{tp.shipmentDetails.notificationTitle}</TableHead>
                        <TableHead className="text-sm">{tp.shipmentDetails.createdDate}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {notifications.map((n) => (
                        <TableRow key={n.id}>
                          <TableCell className="text-sm font-mono text-xs">{n.type}</TableCell>
                          <TableCell className="text-sm">
                            <div className="flex flex-wrap gap-1">
                              {n.channels.map((c) => (
                                <Badge key={c} variant="secondary" className="text-xs">
                                  {c === 'in_app' ? tp.shipmentDetails.channelInApp :
                                   c === 'whatsapp' ? tp.shipmentDetails.channelWhatsapp :
                                   c === 'email' ? tp.shipmentDetails.channelEmail :
                                   c === 'sms' ? tp.shipmentDetails.channelSms : c}
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {n.recipient_role === 'tenant' ? tp.shipmentDetails.customer :
                             n.recipient_role === 'carrier' ? tp.shipmentDetails.carrier :
                             n.recipient_role === 'driver' ? tp.shipmentDetails.tripDriver :
                             n.recipient_role === 'admin' ? tp.shipmentDetails.customer :
                             tp.shipmentDetails.customer}
                          </TableCell>
                          <TableCell className="text-sm">{n.title}</TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(n.created_at, 'dd MMM yyyy - HH:mm')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

        </Tabs>
      </div>

      {/* Map Dialog */}
      <Dialog open={showMapDialog} onOpenChange={setShowMapDialog}>
        <DialogContent className="max-w-3xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">{selectedAddress?.title}</DialogTitle>
          </DialogHeader>
          <div className="h-[400px] w-full">
            {selectedAddress && (
              <ShipmentMap address={{ lat: selectedAddress.lat, lng: selectedAddress.lng, street: '', neighborhood: '', city: '' }} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Driver Assignment Dialog */}
      <Dialog open={showDriverDialog} onOpenChange={setShowDriverDialog}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">{tp.shipmentDetails.assignDriverTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingDrivers ? (
              <LoadingSkeleton type="card" />
            ) : drivers.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">{tp.shipmentDetails.noDriversAvailable}</p>
            ) : (
              drivers.map((driver) => (
                <Card key={driver.id} className="hover:border-primary transition-colors cursor-pointer" onClick={() => handleAssignDriver(driver.id)}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-semibold">{driver.name}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <Phone className="h-3 w-3" />
                          {formatPhone(driver.phone)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {tp.shipmentDetails.currentShipments} <span className="font-medium">{driver.current_shipments_count}</span>
                        </p>
                        {driver.coverage_areas && driver.coverage_areas.length > 0 && (
                          <p className="text-sm text-muted-foreground">
                            {tp.shipmentDetails.coverageAreas} <span className="font-medium">{driver.coverage_areas.join('، ')}</span>
                          </p>
                        )}
                      </div>
                      <Button variant="outline" size="sm">
                        {tp.shipmentDetails.assignDriver}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Retry Delivery Dialog */}
      <Dialog
        open={showRetryDialog}
        onOpenChange={(open) => {
          if (!open) {
            setRetryNote('');
            setRetryScheduledDate(new Date().toISOString().slice(0, 10));
          }
          setShowRetryDialog(open);
        }}
      >
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">{tp.shipmentDetails.retryDialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Alert variant="default" className="border-amber-400 bg-amber-50 dark:bg-amber-950">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
                {tp.shipmentDetails.retryWarning}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="retry_scheduled_date" className="text-sm">
                {tp.shipmentDetails.retryScheduledDate}
              </Label>
              <Input
                id="retry_scheduled_date"
                type="date"
                dir="ltr"
                value={retryScheduledDate}
                onChange={(e) => setRetryScheduledDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="retry_note" className="text-sm">
                {tp.shipmentDetails.retryNotes}
              </Label>
              <Textarea
                id="retry_note"
                rows={3}
                value={retryNote}
                onChange={(e) => setRetryNote(e.target.value)}
                placeholder={tp.shipmentDetails.retryNotesPlaceholder}
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRetryDialog(false)}
              disabled={retrySubmitting}
            >
              {tp.shipmentDetails.cancel}
            </Button>
            <Button
              onClick={handleRetryDelivery}
              disabled={retrySubmitting}
              className="gap-1.5"
            >
              {retrySubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {tp.shipmentDetails.scheduling}
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  {tp.shipmentDetails.scheduleRetry}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Label Dialog */}
      <PrintLabelDialog
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        shipment={shipment}
        labelType={selectedLabelType}
      />
    </PageContainer>
  );
}
